/**
 * Tradutor de mensagens de erro do Supabase para mensagens amigáveis
 *
 * Converte erros técnicos do banco de dados em mensagens compreensíveis
 * para o usuário final.
 */

import { RECONNECTING_MESSAGE, isReconnectingError } from '@/services/errors';

type TranslatedError = { title: string; description: string };

/** JOSH-89: a reconnecting session is a pause, never a technical error. */
const RECONNECTING: TranslatedError = { title: 'Reconectando', description: RECONNECTING_MESSAGE };

// Mapeamento de colunas para nomes amigáveis em português
const COLUMN_NAMES: Record<string, string> = {
  // IDs - Obras
  obra_id: 'Obra',
  obra_ponto_id: 'Ponto',
  obra_area_id: 'Área',
  obra_ambiente_id: 'Ambiente',
  obra_jornada_tipo_id: 'Tipo de Jornada',
  obra_produto_id: 'Produto',
  obra_cabo_id: 'Cabo',
  estado_id: 'Estado',
  // IDs - Tipos
  ambiente_tipo_id: 'Tipo de Ambiente',
  ponto_tipo_id: 'Tipo de Ponto',
  ponto_tipo_grupo_id: 'Grupo',
  servico_tipo_id: 'Tipo de Serviço',
  cabo_tipo_id: 'Tipo de Cabo',
  porta_tipo_id: 'Tipo de Porta',
  porta_categoria_id: 'Categoria da Porta',
  lancamento_tipo_id: 'Tipo de Lançamento',
  movimento_tipo_id: 'Tipo de Movimento',
  oportunidade_tipo_id: 'Tipo de Oportunidade',
  oportunidade_origem_id: 'Origem',
  projeto_status_id: 'Status',
  obra_status_id: 'Status da Obra',
  // IDs - Entidades
  pessoa_id: 'Pessoa',
  produto_id: 'Produto',
  empreendimento_id: 'Empreendimento',
  departamento_id: 'Departamento',
  oportunidade_id: 'Oportunidade',
  proposta_id: 'Proposta',
  projeto_id: 'Projeto',
  lancamento_id: 'Lançamento',
  tarefa_id: 'Tarefa',
  os_id: 'OS',
  canal_id: 'Canal',
  // IDs - Pessoas
  responsavel_id: 'Responsável',
  decisor_id: 'Decisor',
  colaborador_id: 'Colaborador',
  consultor_id: 'Consultor',
  // IDs - Financeiro
  banco_id: 'Banco',
  conta_id: 'Conta',
  categoria_contabil_id: 'Categoria Contábil',
  centro_custo_id: 'Centro de Custo',
  forma_pagamento_id: 'Forma de Pagamento',
  lancamento_categoria_id: 'Categoria',
  // IDs - Sistema
  perfil_id: 'Perfil',
  plano_id: 'Plano',
  modulo_id: 'Módulo',
  marca_id: 'Marca',
  escopo_id: 'Escopo',
  tenant_id: 'Empresa',
  filial_id: 'Filial',
  // IDs - Auditoria
  created_by: 'Criado por',
  updated_by: 'Atualizado por',
  deleted_by: 'Excluído por',
  // Campos - Texto
  nome: 'Nome',
  titulo: 'Título',
  descricao: 'Descrição',
  obs: 'Observações',
  codigo: 'Código',
  // Campos - Contato
  email: 'E-mail',
  telefone: 'Telefone',
  cpf: 'CPF',
  cnpj: 'CNPJ',
  // Campos - Datas
  data: 'Data',
  data_inicio: 'Data de Início',
  data_fim: 'Data de Fim',
  data_prazo: 'Data de Prazo',
  data_vencimento_original: 'Data de Vencimento',
  data_movimento: 'Data do Movimento',
  // Campos - Números
  revisao: 'Revisão',
  qtd: 'Quantidade',
  valor: 'Valor',
  valor_original: 'Valor Original',
  valor_final: 'Valor Final',
  percentual: 'Percentual',
  ordem: 'Ordem',
  num: 'Número',
  // Campos - Status
  status: 'Status',
  tipo: 'Tipo',
  prioridade: 'Prioridade',
  // Campos - Obras
  obra: 'Obra',
  ponto: 'Ponto',
  ambiente: 'Ambiente',
  area: 'Área',
  tubo_nome: 'Nome do Tubo',
  cabo_nome: 'Nome do Cabo',
  // Campos - Endereço
  cidade: 'Cidade',
  estado: 'Estado',
  uf: 'UF',
  // Campos - Financeiro
  ag: 'Agência'
};

// Mapeamento de tabelas para nomes amigáveis
const TABLE_NAMES: Record<string, string> = {
  // Obras
  obra: 'Obra',
  obra_area: 'Área',
  obra_ambiente: 'Ambiente',
  obra_ponto: 'Ponto',
  obra_produto: 'Produto',
  obra_servico: 'Serviço',
  obra_envolvido: 'Envolvido',
  obra_tubo: 'Tubo',
  obra_cabo: 'Cabo',
  obra_jornada: 'Jornada',
  obra_projeto: 'Projeto',
  obra_conexao: 'Conexão',
  // Tipos
  ambiente_tipo: 'Tipo de Ambiente',
  ponto_tipo: 'Tipo de Ponto',
  ponto_tipo_grupo: 'Grupo de Ponto',
  servico_tipo: 'Tipo de Serviço',
  cabo_tipo: 'Tipo de Cabo',
  porta_tipo: 'Tipo de Porta',
  lancamento_tipo: 'Tipo de Lançamento',
  movimento_tipo: 'Tipo de Movimento',
  oportunidade_tipo: 'Tipo de Oportunidade',
  oportunidade_origem: 'Origem',
  // Entidades
  projeto: 'Projeto',
  pessoa: 'Pessoa',
  empreendimento: 'Empreendimento',
  departamento: 'Departamento',
  oportunidade: 'Oportunidade',
  proposta: 'Proposta',
  tarefa: 'Tarefa',
  os: 'OS',
  // Financeiro
  lancamento: 'Lançamento',
  movimento: 'Movimento',
  banco: 'Banco',
  conta: 'Conta',
  categoria_contabil: 'Categoria Contábil',
  centro_custo: 'Centro de Custo',
  forma_pagamento: 'Forma de Pagamento',
  // Sistema
  perfil: 'Perfil',
  filial: 'Filial',
  tenant: 'Empresa'
};

type ErrorLike = { message?: string; error_description?: string } | string | null | undefined | unknown;

const extractMessage = (error: ErrorLike): string => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    const e = error as { message?: unknown; error_description?: unknown };
    if (typeof e.message === 'string') return e.message;
    if (typeof e.error_description === 'string') return e.error_description;
  }
  return String(error);
};

/**
 * Traduz uma mensagem de erro do Supabase para uma mensagem amigável
 */
export const translateError = (error: ErrorLike): TranslatedError => {
  if (isReconnectingError(error)) return RECONNECTING;
  const errorMessage = extractMessage(error);

  // NOT NULL constraint violation
  const notNullMatch = errorMessage.match(/null value in column "(\w+)" of relation "(\w+)" violates not-null constraint/i);
  if (notNullMatch) {
    const [, column, table] = notNullMatch;
    const friendlyColumn = COLUMN_NAMES[column] || column;
    const friendlyTable = TABLE_NAMES[table] || table;
    return {
      title: 'Campo obrigatório',
      description: `O campo "${friendlyColumn}" é obrigatório para ${friendlyTable}.`
    };
  }

  // UNIQUE constraint violation
  const uniqueMatch = errorMessage.match(/duplicate key value violates unique constraint "(\w+)"/i);
  if (uniqueMatch) {
    // Tentar extrair o nome do campo da constraint
    const constraintName = uniqueMatch[1];
    if (constraintName.includes('email')) {
      return {
        title: 'E-mail já cadastrado',
        description: 'Este e-mail já está em uso. Por favor, utilize outro.'
      };
    }
    if (constraintName.includes('cpf')) {
      return {
        title: 'CPF já cadastrado',
        description: 'Este CPF já está em uso no sistema.'
      };
    }
    if (constraintName.includes('cnpj')) {
      return {
        title: 'CNPJ já cadastrado',
        description: 'Este CNPJ já está em uso no sistema.'
      };
    }
    if (constraintName.includes('projeto_obra_revisao')) {
      return {
        title: 'Revisão duplicada',
        description: 'Já existe um projeto com esta revisão para esta obra. Selecione outra revisão.'
      };
    }
    return {
      title: 'Registro duplicado',
      description: 'Já existe um registro com estes dados. Verifique e tente novamente.'
    };
  }

  // Foreign key violation
  const fkMatch = errorMessage.match(/violates foreign key constraint "(\w+)"/i);
  if (fkMatch) {
    return {
      title: 'Referência inválida',
      description: 'O registro selecionado não existe ou foi removido. Atualize a página e tente novamente.'
    };
  }

  // Foreign key violation - still referenced
  const fkRefMatch = errorMessage.match(/update or delete on table "(\w+)" violates foreign key constraint/i);
  if (fkRefMatch) {
    const table = fkRefMatch[1];
    const friendlyTable = TABLE_NAMES[table] || table;
    return {
      title: 'Não é possível excluir',
      description: `Este registro de ${friendlyTable} está sendo utilizado em outros lugares do sistema.`
    };
  }

  // Check constraint violation
  const checkMatch = errorMessage.match(/violates check constraint "(\w+)"/i);
  if (checkMatch) {
    return {
      title: 'Valor inválido',
      description: 'Um dos valores informados não é válido. Verifique os campos e tente novamente.'
    };
  }

  // Row-level security (RLS) violation
  if (errorMessage.includes('new row violates row-level security policy')) {
    return {
      title: 'Sem permissão',
      description: 'Você não tem permissão para realizar esta operação.'
    };
  }

  // Connection/timeout errors
  if (errorMessage.includes('timeout') || errorMessage.includes('ETIMEDOUT')) {
    return {
      title: 'Tempo esgotado',
      description: 'A operação demorou muito. Verifique sua conexão e tente novamente.'
    };
  }

  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return {
      title: 'Erro de conexão',
      description: 'Não foi possível conectar ao servidor. Verifique sua internet.'
    };
  }

  // Permission denied
  if (errorMessage.includes('permission denied') || errorMessage.includes('42501')) {
    return {
      title: 'Acesso negado',
      description: 'Você não tem permissão para realizar esta ação.'
    };
  }

  // Generic database errors
  if (errorMessage.includes('PGRST') || errorMessage.includes('42')) {
    return {
      title: 'Erro ao salvar',
      description: 'Ocorreu um erro ao processar sua solicitação. Tente novamente.'
    };
  }

  // Default fallback
  return {
    title: 'Erro ao salvar',
    description: 'Ocorreu um erro inesperado. Por favor, tente novamente.'
  };
};

/**
 * Verifica se uma mensagem de erro é técnica (não amigável)
 */
export const isTechnicalError = (message: unknown): boolean => {
  if (!message || typeof message !== 'string') return false;

  const technicalPatterns = [
    /violates.*constraint/i,
    /duplicate key/i,
    /foreign key/i,
    /null value in column/i,
    /row-level security/i,
    /PGRST\d+/i,
    /SQL.*error/i,
    /relation ".*" does not exist/i,
    /column ".*" does not exist/i
  ];

  return technicalPatterns.some(pattern => pattern.test(message));
};

/**
 * Processa um erro e retorna uma mensagem amigável
 */
export const getErrorMessage = (error: ErrorLike): TranslatedError => {
  if (isReconnectingError(error)) return RECONNECTING;
  const errorMessage = extractMessage(error);

  // Se já é uma mensagem amigável, retorna como está
  if (!isTechnicalError(errorMessage)) {
    return {
      title: 'Erro',
      description: errorMessage
    };
  }

  // Traduz erro técnico
  return translateError(error);
};

const errorMessageTranslator = {
  translateError,
  isTechnicalError,
  getErrorMessage,
  COLUMN_NAMES,
  TABLE_NAMES
};

export default errorMessageTranslator;
